// apps/web/lib/logger.ts
//
// Wraps `@atriam/observability`'s logger so:
//   1. ERROR-level emissions on Vercel ALSO fire `captureServerException` so
//      they land in PostHog Error Tracking (queryable, with `cause` chain).
//   2. WARN+ emissions on Vercel trigger a `forceFlush()` on the server-side
//      OTel LoggerProvider via Next.js' `after()` so the records survive the
//      post-response function freeze and reach SigNoz. Without this flush,
//      `BatchLogRecordProcessor`'s in-memory queue is killed mid-flight when
//      the function freezes; 48h empirical state was 0 atriam-web server logs
//      in SigNoz despite hundreds of warn calls per day.
//
// Cause-preserving shim: developers commonly call
//   logger.error('label', { message: err.message })
// which loses the original Error (stack, code, hint, cause). To stop this
// from masking real bugs, when `meta.error` is missing but the meta object
// has the shape of a Supabase/PG/Stripe/Plaid error
// (any of message/code/details/hint/status), we wrap it as a synthetic
// Error and attach it as the `cause` of a new label-Error. `captureServerException`
// then flattens the cause chain into top-level event properties so the
// PostHog issue is queryable on `cause_code` / `cause_hint` / etc.

import { logger as baseLogger } from "@atriam/observability";
import type { Logger } from "@atriam/observability";
import { isTransientBackendError } from "@atriam/gateway-runtime/transient-errors";

import { isNextControlFlow } from "./analytics/next-control-flow";

export type { Logger } from "@atriam/observability";

type ServerExceptionCapture = (
  error: unknown,
  context?: { distinctId?: string; properties?: Record<string, unknown> },
) => Promise<void>;

const SERVER_EXCEPTION_CAPTURE_KEY = Symbol.for(
  "atriam.serverExceptionCapture",
);

/**
 * Install the Node-only exception sink during Next.js instrumentation startup.
 * The global symbol survives chunk boundaries without making this isomorphic
 * logger import the server-only PostHog SDK into client compilation graphs.
 */
export function registerServerExceptionCapture(
  capture: ServerExceptionCapture | undefined,
): void {
  if (capture) {
    Reflect.set(globalThis, SERVER_EXCEPTION_CAPTURE_KEY, capture);
  } else {
    Reflect.deleteProperty(globalThis, SERVER_EXCEPTION_CAPTURE_KEY);
  }
}

function getServerExceptionCapture(): ServerExceptionCapture | undefined {
  const capture: unknown = Reflect.get(
    globalThis,
    SERVER_EXCEPTION_CAPTURE_KEY,
  );
  return typeof capture === "function"
    ? (capture as ServerExceptionCapture)
    : undefined;
}

interface ErrorLikeMeta {
  message?: unknown;
  code?: unknown;
  details?: unknown;
  hint?: unknown;
  status?: unknown;
}

function looksLikeError(value: unknown): value is ErrorLikeMeta {
  if (value == null || typeof value !== "object") return false;
  // Require a non-empty `message`. The other fields (code/details/hint/status)
  // only ENRICH a real error — they're not error-like on their own. Previously
  // any object with a bare `status` (an HTTP status, a fetch-Response shape) was
  // treated as an error and wrapped in a content-free `Error('unknown')` that
  // surfaced as noise in PostHog Error Tracking. A genuine PG/Supabase/Stripe
  // error always carries `message`, so this loses no real captures.
  const { message } = value as ErrorLikeMeta;
  return typeof message === "string" && message.length > 0;
}

function buildSyntheticCause(meta: ErrorLikeMeta): Error {
  const msg = typeof meta.message === "string" ? meta.message : "unknown";
  const cause = new Error(msg) as Error & {
    code?: unknown;
    details?: unknown;
    hint?: unknown;
    status?: unknown;
  };
  // Preserve PG/Supabase/fetch-Response fields so `flattenCauseChain` in
  // posthog-server can promote them to top-level event properties.
  if (meta.code !== undefined) cause.code = meta.code;
  if (meta.details !== undefined) cause.details = meta.details;
  if (meta.hint !== undefined) cause.hint = meta.hint;
  if (meta.status !== undefined) cause.status = meta.status;
  return cause;
}

function metaToRecord(meta: unknown): Record<string, unknown> {
  if (meta === null || meta === undefined) return {};
  if (meta instanceof Error) return { error: meta };
  if (typeof meta === "object") return meta as Record<string, unknown>;
  return { meta };
}

/**
 * Build the Error that `captureServerException` will receive. Priority:
 * 1. `meta.error` is an Error → use directly
 * 2. `meta` looks like a Supabase/PG/Stripe error (has message/code/...) →
 *    wrap as cause of a new label-Error
 * 3. Otherwise → bare `new Error(label)` (last resort, content-free)
 */
export function buildCapturedError(message: string, meta: unknown): Error {
  const record = metaToRecord(meta);
  if (record.error instanceof Error) return record.error;
  // Supabase's PostgrestError is a POJO, not an Error subclass — so callers
  // doing `logger.error("label", { error: pgErr })` would otherwise fall
  // through to a content-free bare Error. Recognise the PG/Stripe/fetch shape
  // on `record.error` and wrap it as cause so `flattenCauseChain` can promote
  // its `code`/`details`/`hint`/`status` to top-level event properties.
  if (
    record.error != null &&
    typeof record.error === "object" &&
    !(record.error instanceof Error) &&
    looksLikeError(record.error)
  ) {
    const cause = buildSyntheticCause(record.error);
    return new Error(message, { cause });
  }
  if (looksLikeError(record)) {
    const cause = buildSyntheticCause(record);
    return new Error(message, { cause });
  }
  return new Error(message);
}

/**
 * Schedule a forceFlush of the OTel LoggerProvider after the response is
 * sent. Vercel `after()` keeps the function alive long enough for the OTLP
 * POST to complete; without it `BatchLogRecordProcessor` would be killed
 * mid-flight when the function freezes.
 *
 * `after()` MUST be called synchronously from the logger call, while the
 * request scope is still open. The previous version awaited two dynamic
 * imports first — for the common `logger.info(...); return res` shape the
 * handler had already returned by the time `after()` ran, the call threw
 * "outside a request scope", and the fallback's un-awaited flush lost the
 * race against the freeze. Empirically that made audit-log delivery
 * traffic-dependent (~1/day instead of ~288/day) while the in-request
 * otel-debug flush worked instantly (2026-06-10 diagnosis). So both modules
 * are preloaded at module-load on the server and the schedule path is fully
 * synchronous, mirroring how posthog-server's after() flush (which always
 * worked) is wired.
 *
 * Node-runtime + Vercel gating mirrors the PostHog-shim gate below so dev
 * (no VERCEL env) doesn't pay the per-emit flush cost when the logs are
 * already going to the dev OTLP endpoint that doesn't freeze.
 */
type AfterFn = (task: () => Promise<void>) => void;
let afterRef: AfterFn | undefined;
let flushRef: (() => Promise<void>) | undefined;

if (
  typeof window === "undefined" &&
  typeof process !== "undefined" &&
  process.env.NEXT_RUNTIME === "nodejs"
) {
  // Preload at module evaluation (cold start) so scheduleServerLogFlush can
  // call after() synchronously inside the request that logs. Both resolve
  // long before the first logger call in practice; until then the fallback
  // below still fires a best-effort inline flush.
  void import("@atriam/observability/server-logger-provider")
    .then((mod) => {
      flushRef = mod.flushServerLogs;
    })
    .catch(() => {});
  void import("next/server")
    .then((mod) => {
      afterRef = mod.after;
    })
    .catch(() => {});
}

function scheduleServerLogFlush(): void {
  if (typeof process === "undefined") return;
  if (process.env.NEXT_RUNTIME !== "nodejs") return;
  if (!process.env.VERCEL) return;
  const flush = (): Promise<void> =>
    flushRef ? flushRef().catch(() => {}) : Promise.resolve();
  if (afterRef) {
    try {
      afterRef(flush);
      return;
    } catch {
      // Outside a request scope (unit tests, scripts, module-load warns) —
      // fall through to the inline best-effort flush.
    }
  }
  void flush();
}

function maybeCaptureToPostHog(message: string, meta: unknown): void {
  // Drop Next.js framework signals (dynamic-server-usage bailouts, redirect,
  // notFound) regardless of which side we're on. `onRequestError` filters
  // these on the server, but any catch site that re-emits via `logger.error`
  // would otherwise bypass that filter. The bailout error is either `meta`
  // itself, `meta.error`, or `meta.cause` — check all three.
  const record = metaToRecord(meta);
  if (
    isNextControlFlow(meta) ||
    isNextControlFlow(record.error) ||
    isNextControlFlow((record as { cause?: unknown }).cause)
  ) {
    return;
  }

  // Browser path: dual-dispatch to PostHog via `captureClientException`. The
  // SDK gates itself on `posthog.init` having been called (it stays a no-op
  // until then), so this is safe to invoke unconditionally. Without this,
  // every `logger.error` from a client component or hook went to SigNoz only
  // and stayed out of PostHog's queryable Issues view.
  if (typeof window !== "undefined") {
    void (async () => {
      try {
        const mod = await import("./analytics/capture-exception");
        const captured = buildCapturedError(message, meta);
        if (isNextControlFlow(captured.cause)) return;
        const { error: _omit, ...rest } = record;
        mod.captureClientException(captured, {
          via: "logger.error",
          logMessage: message,
          ...rest,
        });
      } catch {
        /* swallow — telemetry must never break the caller */
      }
    })();
    return;
  }

  // Server path: dual-dispatch to PostHog via `captureServerException`. Gated
  // to Vercel + nodejs runtime so local `pnpm dev` doesn't pollute prod data.
  if (typeof process === "undefined") return;
  if (process.env.NEXT_RUNTIME !== "nodejs") return;
  if (!process.env.VERCEL) return;

  const captureServerException = getServerExceptionCapture();
  if (!captureServerException) return;
  const captured = buildCapturedError(message, meta);
  if (isNextControlFlow(captured.cause)) return;
  const { error: _omit, ...rest } = record;
  void captureServerException(captured, {
    properties: {
      via: "logger.error",
      logMessage: message,
      ...rest,
    },
  }).catch(() => {
    /* telemetry must never break the caller */
  });
}

function isAuditTagged(meta: unknown): boolean {
  return (
    meta != null &&
    typeof meta === "object" &&
    !(meta instanceof Error) &&
    (meta as { audit?: unknown }).audit === true
  );
}

/**
 * A transient backend-unreachable error (Kong/Envoy connect timeout, 5xx edge,
 * socket reset) recovers on the next cron tick. Detect it on the meta payload —
 * either `meta` itself, `meta.error`, or its `.cause` — so a `logger.error`
 * raised by a pipeline catch is demoted to a SigNoz WARN and kept out of the
 * PostHog issue tracker. Without this, every pg_cron tick during a Supabase
 * blip became a `$exception` (the 2026-06-07 ~5,400-event flood).
 */
function metaIsTransientBackend(meta: unknown): boolean {
  if (isTransientBackendError(meta)) return true;
  const record = metaToRecord(meta);
  if (isTransientBackendError(record.error)) return true;
  if (isTransientBackendError((record as { cause?: unknown }).cause))
    return true;
  return false;
}

export const logger: Logger = {
  ...baseLogger,
  // `info` and `debug` are normally console-only, but when the caller tags a
  // record with `{ audit: true }` the underlying base logger bypasses the
  // WARN floor and ships to OTel. On Vercel the OTel batch queue gets killed
  // when the function freezes unless we schedule a flush via after(). Without
  // this hook every pipeline-heartbeat info log was queued and discarded
  // mid-flight — the whole heartbeat layer was silently invisible in SigNoz.
  info(message, meta) {
    baseLogger.info(message, meta);
    if (isAuditTagged(meta)) scheduleServerLogFlush();
  },
  debug(message, meta) {
    baseLogger.debug(message, meta);
    if (isAuditTagged(meta)) scheduleServerLogFlush();
  },
  warn(message, meta) {
    baseLogger.warn(message, meta);
    scheduleServerLogFlush();
  },
  error(message, meta) {
    if (metaIsTransientBackend(meta)) {
      // Self-healing backend blip: SigNoz WARN, no PostHog issue, no page.
      baseLogger.warn(message, metaToRecord(meta));
      scheduleServerLogFlush();
      return;
    }
    baseLogger.error(message, meta);
    // Schedule both the OTel flush (for SigNoz) and the PostHog capture
    // (for the issue tracker). They run independently so a failure in one
    // doesn't block the other.
    scheduleServerLogFlush();
    maybeCaptureToPostHog(message, meta);
  },
};
